import { MenuItem as Item, MenuItems as Items, Menu, MenuButton, Transition } from '@headlessui/react'
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import { map } from 'lodash'
import { ComponentProps, MouseEventHandler, ReactNode } from 'react'

import cn from '@/utilities/cn'

type MenuComponentProps = {
  icon?: ReactNode
  direction?: 'up' | 'down' | 'down right'
  className?: string
  children?: ReactNode
  onClick?: MouseEventHandler
  title?: string
}

export default function MenuComponent({
  children,
  icon,
  className,
  onClick,
  direction = 'down',
  title,
  ...rest
}: MenuComponentProps) {
  return (
    <Menu as="div" className="relative inline-block text-left" title={title}>
      <MenuButton
        onClick={onClick}
        data-testid="menu-button"
        className={cn('flex cursor-pointer items-center justify-center', className)}
        {...rest}
      >
        {icon || <EllipsisVerticalIcon className="h-7 w-7 hover:stroke-zinc-400" />}
      </MenuButton>

      <Transition
        enter="transition duration-100 ease-out"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Items
          className={cn(
            'absolute z-50 mt-2 w-48 divide-y divide-zinc-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
            direction === 'up'
              ? 'right-6 bottom-6 origin-bottom-right'
              : direction === 'down right'
                ? 'ltr ml-5'
                : 'right-0 origin-top-right'
          )}
        >
          {children}
        </Items>
      </Transition>
    </Menu>
  )
}

export function MenuItem(props: ComponentProps<'button'>) {
  return (
    <Item>
      {({ focus }) => (
        <button
          {...props}
          className={cn(
            focus ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-700',
            'group flex w-full items-center px-4 py-2 text-sm',
            props.className
          )}
        />
      )}
    </Item>
  )
}

export type MenuItem = {
  onClick: (rowData: any) => void
  label: ReactNode
}
export type MenuItemProps = {
  items: MenuItem[]
  className?: string
}
export function MenuItems({ items, className }: MenuItemProps) {
  return (
    <div className={cn(className)}>
      {map(items, (item, index) => (
        <MenuItem key={index} onClick={item.onClick} id={`${index}`}>
          {item.label}
        </MenuItem>
      ))}
    </div>
  )
}
